import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as queueApi from "../../api/queueApi";
import { failureMessage } from "../../api/client";
import { createSession, refreshSessionData, sessionError, setSessionCode } from "../../actions/sessionActions";
import { adoptHostKey } from "../../actions/hostActions";
import { SESSION_CODE_LENGTH, normalizeSessionCode } from "../../config";
import { HOST_KEY_LENGTH, looksLikeHostKey, normalizeHostKey } from "../../session/hostKey";
import { hasSessionCache } from "../../offline/sessionCache";

// injected at build time, see vite.config.js
const buildDate = __BUILD_DATE__;

const hostKeyMessage = (err) => {
  if (err.code === "INVALID_HOST_KEY") return "That is not a valid host key";
  if (err.response?.status === 404) return "That host key is not known";
  return "Could not reach the server, please try again";
};

/** The start page: a session code or a host key, a new session, or back into the last one. */
const HomePage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const previousCode = useSelector((state) => state.session.Code);
  const previousRole = useSelector((state) => state.session.isHost);
  const error = useSelector((state) => state.session.error);

  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const keyFromLink = searchParams.get("key");
  const handledLink = useRef(false);

  const join = async (value) => {
    const sessionCode = normalizeSessionCode(value);
    if (!sessionCode || busy) return;

    setBusy(true);
    try {
      await queueApi.getQueue(sessionCode);
      dispatch(setSessionCode(sessionCode));
      navigate("/lyrics", { replace: true });
    } catch (err) {
      // no answer at all, but the session is on this device: carry on from the copy
      if (!err.response && hasSessionCache(sessionCode)) {
        dispatch(setSessionCode(sessionCode));
        navigate("/lyrics", { replace: true });
        return;
      }
      dispatch(
        sessionError(
          err.response?.status === 404
            ? `No session with code ${sessionCode}`
            : failureMessage(err, "Could not reach the server, please try again")
        )
      );
    } finally {
      setBusy(false);
    }
  };

  // A host key opens the host's current session on this device, or starts one.
  const enterHostKey = async (value) => {
    if (busy) return;
    setBusy(true);
    try {
      const sessionCode = await dispatch(adoptHostKey(value));
      if (sessionCode) {
        dispatch(setSessionCode(sessionCode));
        // the role may have changed even when this is already the current session
        dispatch(refreshSessionData({ force: true }));
        navigate("/lyrics", { replace: true });
      } else {
        await dispatch(createSession());
        navigate("/welcome", { replace: true });
      }
    } catch (err) {
      dispatch(sessionError(hostKeyMessage(err)));
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!keyFromLink || handledLink.current) return;
    handledLink.current = true;
    enterHostKey(keyFromLink);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once for the key in the link
  }, [keyFromLink]);

  const onCodeChange = (event) => {
    const raw = event.target.value;
    const hostMode = looksLikeHostKey(raw);
    const value = hostMode
      ? raw.trim().toUpperCase().slice(0, HOST_KEY_LENGTH)
      : normalizeSessionCode(raw).slice(0, SESSION_CODE_LENGTH);
    setCode(value);
    if (error) dispatch(sessionError(null));
    if (hostMode) {
      if (normalizeHostKey(value)) enterHostKey(value);
    } else if (value.length === SESSION_CODE_LENGTH) {
      join(value);
    }
  };

  const onSubmit = (event) => {
    event.preventDefault();
    if (looksLikeHostKey(code)) enterHostKey(code);
    else join(code);
  };

  const create = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await dispatch(createSession());
      navigate("/welcome");
    } catch (err) {
      dispatch(sessionError(failureMessage(err, "Could not create a session, please try again")));
      setBusy(false);
    }
  };

  const hostMode = looksLikeHostKey(code);

  return (
    <div className="start" data-screen-label="Start">
      <div>
        <div className="start-brand">Sing At</div>
        <div className="start-tag">Shared live lyrics</div>
      </div>
      <div className="start-body">
        <form className={`card${hostMode ? " host-mode" : ""}`} onSubmit={onSubmit}>
          <div className="kicker kicker-lg" style={{ marginBottom: 10 }}>
            {hostMode ? "Host key" : "Session code"}
          </div>
          <input
            id="sessionCode"
            className={`code-field${hostMode ? " host-mode" : ""}`}
            aria-label="Session code"
            value={code}
            onChange={onCodeChange}
            maxLength={hostMode ? HOST_KEY_LENGTH : SESSION_CODE_LENGTH}
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            placeholder="CODE"
            disabled={busy}
          />
          <p className="note" style={{ marginTop: 10 }}>
            A session code, or your host key (HOST-…) to run your session here.
          </p>
          {error && (
            <p className="error-text" role="alert" style={{ marginTop: 8 }}>
              {error}
            </p>
          )}
          <button type="submit" className={`btn btn-md btn-block ${code.length ? "btn-dark" : "btn-soft"}`} disabled={busy || code.length === 0} style={{ marginTop: 14 }}>
            {hostMode ? "Use host key" : "Join"}
          </button>
        </form>
        <button type="button" className="btn btn-lg btn-card" onClick={create} disabled={busy}>
          Create a new session
        </button>
        {previousCode && (
          <button type="button" className="btn btn-md btn-outline btn-rejoin" onClick={() => join(previousCode)} disabled={busy}>
            <span className="muted">Rejoin</span> <span className="rejoin-code">{previousCode}</span>{" "}
            <span className="rejoin-role">{previousRole ? "as host" : "as guest"}</span>
          </button>
        )}
      </div>
      <div className="start-foot">Built {buildDate}</div>
    </div>
  );
};

export default HomePage;
